// COMPONENTS & FUNCTIONS
import {
  calculateHandler,
  convertHandler,
} from "./mathHandler";
import { removeFormatLargeNumbers } from "./textFormatHandler";
import { calculateCalculatorGraph } from "../engineering/calculations/calculatorGraph";
import {
  calculateSeriesGraph,
  GraphLegendResult,
} from "../engineering/calculations/graph";

// INTERFACES
import {
  AdvancedCalculatorChartAxis,
  AdvancedCalculatorResult,
  GraphConfiguration,
  GraphLegend,
  GraphVariable,
} from "../interface/functionalityInterface";
import {
  AdvancedCalculatorUnitProps,
  CalculatorUnitProps,
} from "../interface/propTypesInterface";

// NPM INSTALLS
import { v4 as UUID } from "uuid";

export interface GraphSourceVariable extends GraphVariable {
  canUseAsX: boolean;
  canUseAsY: boolean;
}

export interface GraphSource {
  id: string;
  title: string;
  contextKey: string;
  noDecimals: number;
  variables: GraphSourceVariable[];
  defaultConfiguration: GraphConfiguration;
  calculateLegend: (
    legend: GraphLegend,
    configuration: GraphConfiguration
  ) => GraphLegendResult;
}

const colors = [
  "#001e1d",
  "#777777",
  "#0e5146",
  "#cb454b",
  "#e2b13c",
  "#307ef6",
];

const labelFor = (name: string) => name.replaceAll("_", " ");

const currentNumber = (value: unknown) => {
  const parsed = calculateHandler(
    removeFormatLargeNumbers(String(value)).replace(",", ".")
  );
  return parsed !== null && Number.isFinite(parsed) ? parsed : null;
};

const defaultControls = (
  xMin: number,
  xMax: number,
  noDecimals: number
) => {
  const safeXMin = Number.isFinite(xMin) ? xMin : 0;
  const safeXMax =
    Number.isFinite(xMax) && xMax > safeXMin
      ? xMax
      : safeXMin + 100;
  const xRange = safeXMax - safeXMin;

  return {
    xMin: safeXMin,
    xMax: safeXMax,
    yMin: null,
    yMax: null,
    xDelta: xRange / 10,
    trackingIncrement: xRange / 100,
    xDecimals: noDecimals,
  };
};

const newLegend = (
  xKey: string,
  yKey: string,
  index = 0,
  borderDash?: number[]
): GraphLegend => ({
  id: UUID(),
  xKey,
  yKey,
  color: colors[index % colors.length],
  borderDash,
});

const selectedDatasetUnit = (
  calculator: AdvancedCalculatorUnitProps,
  dataset: AdvancedCalculatorChartAxis
) => {
  if (!dataset.selectedUnitFromInput) return dataset.selectedUnit;
  return (
    calculator.inputs.find(
      (input) => input.name === dataset.selectedUnitFromInput
    )?.selectedUnit ?? dataset.selectedUnit
  );
};

export const createCalculatorGraphSource = (
  calculator: CalculatorUnitProps
): GraphSource => {
  const mode = calculator.inputs[calculator.selectedCalcMode];
  const equations =
    calculator.calculate[calculator.selectedCalcMode]?.equations ?? [];
  const yKeys = equations.map((equation) => equation.target);
  const variables = mode.baseQuantities
    .filter((quantity) => quantity.type !== "hidden")
    .map((quantity) => ({
      key: quantity.name,
      label: labelFor(quantity.name),
      baseUnit: quantity.baseUnit,
      selectedUnit: quantity.selectedUnit,
      units: quantity.units,
      canUseAsX: true,
      canUseAsY: yKeys.includes(quantity.name),
    }));
  const initialY =
    variables.find((variable) => variable.canUseAsY)?.key ?? "";
  const initialEquation = equations.find(
    (equation) => equation.target === initialY
  );
  const initialX =
    variables.find(
      (variable) =>
        variable.key !== initialY &&
        initialEquation?.expression.includes(variable.key)
    )?.key ??
    variables.find((variable) => variable.key !== initialY)?.key ??
    "";
  const initialXQuantity = mode.baseQuantities.find(
    (quantity) => quantity.name === initialX
  );
  const currentX = Math.abs(
    currentNumber(initialXQuantity?.value) ?? 0
  );
  const xMax = currentX > 0 ? currentX * 2 : 100;
  const unitSignature = variables
    .map(
      (variable) => `${variable.key}:${variable.selectedUnit}`
    )
    .join("|");
  const contextKey = `calculator:${mode.id}:${unitSignature}`;
  const configuration: GraphConfiguration = {
    contextKey,
    activeTab: "legends",
    isPointsPanelOpen: false,
    legends:
      initialX && initialY ? [newLegend(initialX, initialY)] : [],
    controls: defaultControls(0, xMax, calculator.noDecimals),
    savedPoints: [],
  };

  return {
    id: calculator.id,
    title: `${calculator.name} graph`,
    contextKey,
    noDecimals: calculator.noDecimals,
    variables,
    defaultConfiguration: configuration,
    calculateLegend: (legend, graphConfiguration) =>
      calculateCalculatorGraph(
        calculator,
        legend,
        graphConfiguration.controls
      ),
  };
};

export const createAdvancedGraphSource = (
  calculator: AdvancedCalculatorUnitProps,
  result: AdvancedCalculatorResult
): GraphSource | null => {
  if (!calculator.chart || !result.series) return null;

  const xVariable: GraphSourceVariable = {
    ...calculator.chart.xAxis,
    selectedUnit: selectedDatasetUnit(
      calculator,
      calculator.chart.xAxis
    ),
    canUseAsX: true,
    canUseAsY: true,
  };
  const datasetVariables = calculator.chart.datasets.map((dataset) => ({
    key: dataset.key,
    label: dataset.label,
    baseUnit: dataset.baseUnit,
    selectedUnit: selectedDatasetUnit(calculator, dataset),
    units: dataset.units,
    canUseAsX: true,
    canUseAsY: true,
  }));
  const variables = [xVariable, ...datasetVariables].filter(
    (variable, index, allVariables) =>
      allVariables.findIndex(
        (candidate) => candidate.key === variable.key
      ) === index
  );
  const convertedXValues = result.series
    .map((row) =>
      convertHandler(
        row[xVariable.key].toString(),
        xVariable.selectedUnit,
        xVariable.baseUnit,
        xVariable.units
      )
    )
    .filter(Number.isFinite);
  const xMin = Math.min(...convertedXValues);
  const xMax = Math.max(...convertedXValues);
  const unitSignature = variables
    .map(
      (variable) => `${variable.key}:${variable.selectedUnit}`
    )
    .join("|");
  const contextKey = `advanced:${calculator.definitionId}:${unitSignature}`;
  const configuration: GraphConfiguration = {
    contextKey,
    activeTab: "legends",
    isPointsPanelOpen: false,
    legends: calculator.chart.datasets.map((dataset, index) =>
      newLegend(
        calculator.chart!.xAxis.key,
        dataset.key,
        index,
        dataset.borderDash
      )
    ),
    controls: defaultControls(
      xMin,
      xMax,
      calculator.noDecimals
    ),
    savedPoints: [],
  };

  return {
    id: calculator.id,
    title: calculator.chart.title,
    contextKey,
    noDecimals: calculator.noDecimals,
    variables,
    defaultConfiguration: configuration,
    calculateLegend: (legend, graphConfiguration) =>
      calculateSeriesGraph(
        result.series!,
        variables,
        legend,
        graphConfiguration.controls
      ),
  };
};

export const resolveGraphConfiguration = (
  source: GraphSource,
  savedConfiguration?: GraphConfiguration
): GraphConfiguration => {
  if (
    !savedConfiguration ||
    savedConfiguration.contextKey !== source.contextKey
  ) {
    return source.defaultConfiguration;
  }

  return {
    ...source.defaultConfiguration,
    ...savedConfiguration,
    controls: {
      ...source.defaultConfiguration.controls,
      ...savedConfiguration.controls,
    },
    isPointsPanelOpen: savedConfiguration.isPointsPanelOpen ?? false,
    savedPoints: savedConfiguration.savedPoints ?? [],
    legends: savedConfiguration.legends.filter(
      (legend) =>
        source.variables.some(
          (variable) => variable.key === legend.xKey
        ) &&
        source.variables.some(
          (variable) => variable.key === legend.yKey
        )
    ),
  };
};

export const createEmptyGraphLegend = (
  source: GraphSource,
  index: number
): GraphLegend => {
  const xKey =
    source.variables.find((variable) => variable.canUseAsX)?.key ?? "";
  const yKey =
    source.variables.find(
      (variable) =>
        variable.canUseAsY && variable.key !== xKey
    )?.key ?? "";
  return newLegend(xKey, yKey, index);
};
